import api from './api'

export interface CountryConfig {
  countryCode: string
  countryName: string
  primaryIdentifierType: string
  identifierLabel: string
  identifierFormat: string
  identifierExample: string
  identifierLengthMin: number
  identifierLengthMax: number
  validationApiAvailable: boolean
}

export interface CompanyData {
  name: string
  tradingName?: string
  countryCode: string
  primaryIdentifierType?: string
  primaryIdentifier?: string
  email?: string
  phone?: string
  address?: {
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  }
}

export interface CreateCompanyRequest {
  tenantId: string
  companyData: CompanyData
  settings?: {
    currency?: string
    language?: string
    timezone?: string
  }
  billing?: any
  role?: 'admin' | 'user'
}

export interface CreateCompanyResponse {
  actorId: string
  companyId: string
  settingsId: string
}

/**
 * Get all available country configurations
 */
export async function getCountryConfigs(): Promise<CountryConfig[]> {
  const response = await api.get('/companies/countries')
  return response.data
}

/**
 * Get specific country configuration
 */
export async function getCountryConfig(countryCode: string): Promise<CountryConfig> {
  const response = await api.get(`/companies/countries/${countryCode}`)
  return response.data
}

/**
 * Create a new shipper company
 */
export async function createShipper(data: CreateCompanyRequest): Promise<CreateCompanyResponse> {
  const response = await api.post('/companies/shippers', data)
  return response.data
}

/**
 * Create a new freight forwarder company
 */
export async function createFreightForwarder(data: CreateCompanyRequest): Promise<CreateCompanyResponse> {
  const response = await api.post('/companies/freight-forwarders', data)
  return response.data
}

/**
 * Get company by ID
 */
export async function getCompanyById(id: string, type: 'shipper' | 'freight-forwarder'): Promise<any> {
  const response = await api.get(`/companies/${id}?type=${type}`)
  return response.data
}

/**
 * Get all companies (shippers + freight forwarders)
 */
export async function getAllCompanies(tenantId?: string): Promise<any[]> {
  const url = tenantId ? `/companies?tenantId=${tenantId}` : '/companies'
  const response = await api.get(url)
  return response.data
}

/**
 * Create a new shipper with invite (for master panel)
 * Creates company and sends invite email to the provided email
 */
export interface CreateShipperWithInviteRequest {
  companyData: CompanyData & { email: string; contactName?: string }
}

export async function createShipperWithInvite(data: CreateShipperWithInviteRequest): Promise<{
  message: string
  tenantId: string
  actorId: string
  companyId: string
  settingsId: string
  inviteId: string
  inviteToken: string
}> {
  const response = await api.post('/master/companies/shipper', data)
  return response.data
}
